import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild, inject } from '@angular/core';

interface ChatMessage {
  sender: 'agent' | 'user';
  text: string;
  createdAt: Date;
}

@Component({
  selector: 'app-chat-overlay',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-overlay.component.html',
  styleUrl: './chat-overlay.component.scss',
})
export class ChatOverlayComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('messageList')
  private messageList?: ElementRef<HTMLDivElement>;

  isOpen = false;
  isTyping = false;
  draft = '';

  quickPrompts = [
    'How do I file leave?',
    'Where can I view my payslips?',
    'How do I submit DTR?',
  ];

  messages: ChatMessage[] = [
    {
      sender: 'agent',
      text: 'Hi, I am Trieon. I can help you navigate leaves, overtime, DTR, and payslips.',
      createdAt: new Date(),
    },
  ];

  toggleOpen(): void {
    this.isOpen = !this.isOpen;
    this.cdr.markForCheck();

    if (this.isOpen) {
      this.scrollToBottomSoon();
    }
  }

  sendDraft(): void {
    const text = this.draft.trim();
    if (!text || this.isTyping) {
      return;
    }

    this.messages = [...this.messages, { sender: 'user', text, createdAt: new Date() }];
    this.draft = '';
    this.isTyping = true;
    this.cdr.markForCheck();
    this.scrollToBottomSoon();

    window.setTimeout(() => {
      const reply = this.buildMockReply(text);
      this.messages = [...this.messages, { sender: 'agent', text: reply, createdAt: new Date() }];
      this.isTyping = false;
      this.cdr.markForCheck();
      this.scrollToBottomSoon();
    }, 550);
  }

  sendQuickPrompt(prompt: string): void {
    this.draft = prompt;
    this.sendDraft();
  }

  private buildMockReply(input: string): string {
    const q = input.toLowerCase();

    if (q.includes('leave')) {
      return 'Go to My Menu > Leave Requests, then click File Leave to submit your request.';
    }

    if (q.includes('overtime') || q.includes('ot')) {
      return 'Open My Menu > Overtime Requests to file and track overtime status.';
    }

    if (q.includes('dtr') || q.includes('time record')) {
      return 'You can submit DTR from My Menu > Daily Time Record. Use View DTR from dashboard quick actions for faster access.';
    }

    if (q.includes('payslip') || q.includes('payroll')) {
      return 'Your payslips are available in My Menu > My Payslips. You can also view the latest payroll summary on the dashboard.';
    }

    if (q.includes('announcement') || q.includes('event')) {
      return 'Check My Menu > Announcements for company announcements and events relevant to your department.';
    }

    return 'I am still in UI-only mode. Next step is wiring me to the backend chatbot endpoint for smarter answers.';
  }

  private scrollToBottomSoon(): void {
    window.setTimeout(() => {
      const el = this.messageList?.nativeElement;
      if (!el) {
        return;
      }

      el.scrollTop = el.scrollHeight;
    });
  }
}
